import React, { useState, useEffect } from 'react';
import usePaginationValuesWhere from "hooks/usePaginationValuesWhere";
import useExceptionHandler from "hooks/useExceptionHandler";
import useMessage from "hooks/useMessage";
import { GridView, SvgComponent, Simplebar, InputSidebarContainer } from 'components';
import IconButton from "components/IconButton/IconButton";
import ToolTip from "components/ToolTip/ToolTip";
import ListHeader from "components/PantallaList/Header/ListHeader";
import { RequestLogManager } from "managers";
import EditSection from "components/PantallasEdit/EditSection/EditSection";
import SectionRow from "components/PantallasEdit/SectionRow/SectionRow";
import { Form } from "react-bootstrap";
import XMLViewer from "react-xml-viewer";
import ReactJson from "react-json-view";
import { DateHelper, DEFAULT_PAGINATION_VALUES } from "utils";

export default function RequestLogList({ showLoading, hideLoading }) {
  const [where, setWhere] = useState({});
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION_VALUES);
  const [list, setList] = useState([]);
  const [currentRequestLogId, setCurrentRequestLogId] = useState({});
  const [showCanvas, setShowCanvas] = useState(false);
  const { setPaginationValuesWhere } = usePaginationValuesWhere();
  const { handleException } = useExceptionHandler();
  const { cerrarAlertas } = useMessage();

  useEffect(() => {
    buscar();
    return () => {
      cerrarAlertas();
      hideLoading();
    };
  }, [where]);

  const buscar = () => {
    const whereWithPaginationValues = { ...where, ...pagination };

    RequestLogManager.listado(whereWithPaginationValues)
      .then((res) => {
        for (let requestLog of res.Data) {
          requestLog["FechaCreacion"] = DateHelper.formatStringDate(
            requestLog["FechaCreacion"]
          );
        }
        setPaginationValuesWhere(res.Headers, pagination, setPagination);
        setList(res.Data);
      })
      .catch((error) => {
        handleException(error);
      })
      .finally(() => {
        hideLoading();
      });
  };

  const onPageIndexChanging = (pageNumber) => {
    setPagination((prevstate) => {
      return { ...prevstate, IndiceDePagina: pageNumber - 1 };
    });
    setWhere({ ...where });
  };

  const handleClick = (params) => {
    const { Id } = params;
    setCurrentRequestLogId(Id);
    setShowCanvas(true);
  };

  return (
    <>
      <div style={{ paddingRight: "6px" }}>
        <ListHeader titulo={"Request Log"}></ListHeader>
      </div>
      <ViewRequestLog
        requestLogId={currentRequestLogId}
        showCanvas={showCanvas}
        setShowCanvas={setShowCanvas}
        showLoading={showLoading}
        hideLoading={hideLoading}
      />
      <GridView
        pagination={pagination}
        setPagination={(p) => {
          setPagination(p);
        }}
        allowPaging={pagination.EsPaginable}
        pageSize={pagination.CantidadPorPagina}
        pageIndex={pagination.IndiceDePagina}
        lastPageNumber={pagination.PaginasTotal}
        dataSource={list}
        onPageIndexChanging={onPageIndexChanging}
        where={where}
        setWhere={setWhere}
        primaryKey="Id"
        height="80vh"
      >
        <GridView.Column
          showClearFilter={true}
          bodyRenderer={(row) => {
            return (
              <div className="float-right">
                <IconButton onClick={() => handleClick({ Id: row.Id })}>
                  <ToolTip message="Ver">
                    <div>
                      <SvgComponent
                        iconName="view"
                        height="25"
                        width="25"
                      />
                    </div>
                  </ToolTip>
                </IconButton>
              </div>
            );
          }}
        />
        <GridView.Column
          headerText="CompaniaId"
          dataField="CompaniaId"
          sortExpression="CompaniaId"
        />
        <GridView.Column
          headerText="Id"
          dataField="IdSec"
          sortExpression="IdSec"
        />
        <GridView.Column
          headerText="Fecha Creacion"
          dataField="FechaCreacion"
          sortExpression="FechaCreacion"
        />
        <GridView.Column
          headerText="Metodo"
          dataField="Method"
          sortExpression="Method"
        />
        <GridView.Column
          headerText="Endpoint"
          dataField="EndPoint"
          sortExpression="EndPoint"
        />
      </GridView>
    </>
  );
}

function ViewRequestLog({
  showCanvas,
  setShowCanvas,
  requestLogId,
  showLoading,
  hideLoading
}) {

  const [details, setDetails] = useState({});
  const { Message } = useMessage();
  const [isLoading, setIsLoading] = useState(false);
  const [showModal] = useState(false);

  useEffect(() => {
    if (showCanvas && requestLogId) {
      showLoading();
      setIsLoading(true);
      setDetails({});
      RequestLogManager.buscarPorId(requestLogId)
        .then((data) => {
          setDetails(data);
        })
        .catch((error) => {
          handleException(error);
          setShowCanvas(false);
        })
        .finally(() => {
          hideLoading();
          setIsLoading(false);
        });
    }
  }, [showCanvas, requestLogId]);

  const handleCopyToClipboard = (content) => {
    if (!content) return;
    const textToCopy = typeof content === 'string'
      ? content
      : JSON.stringify(content, null, 2);

    navigator.clipboard.writeText(textToCopy).then(() => {
      Message({ message: `Copiado al portapapeles` });
    }).catch(err => {
      console.error("Error al copiar:", err);
      Message({ message: `No se pudo copiar el contenido.` });
    });
  };

  return (
    <Simplebar
      backdrop={true}
      size="lg"
      placement="end"
      portal={false}
      visible={showCanvas}
      setShow={(e) => {
        setShowCanvas(e);
      }}
      useLoading={isLoading}
      title="Detalles del Request Log"
      btnRight={{
        show: false,
        label: "Grabar",
        func: () => { },
      }}
      btnLeft={{
        show: false,
        label: "Cerrar",
      }}
    >
      {showModal}
      <EditSection title="Informacion adicional">
        <SectionRow>
          <InputSidebarContainer label="Direccion IP">
            <Form.Control
              value={details.DireccionIP || ''}
              disabled={true}
            ></Form.Control>
          </InputSidebarContainer>
        </SectionRow>
        <InputSidebarContainer label="Contenido">
          {details.Contenido && typeof details.Contenido === "string" && details.Contenido.trim().startsWith("<") ? (
            <div className="col-sm-12">
              <XMLViewer
                xml={details.Contenido}
                theme={{ overflowBreak: true }}
              />
            </div>
          ) : details.Contenido && (() => {
            let parsed;
            try {
              parsed = typeof details.Contenido === "object"
                ? details.Contenido
                : JSON.parse(details.Contenido);
            } catch {
              parsed = null;
            }
            if (parsed && typeof parsed === "object") {
              return (
                <div className="col-sm-12">
                  <ReactJson
                    src={parsed}
                    name={false}
                    collapsed={false}
                    enableClipboard={false}
                    displayDataTypes={false}
                    style={{ fontSize: "14px" }}
                  />
                </div>
              );
            } else {
              return (
                <Form.Control
                  as="textarea"
                  value={details.Contenido}
                  rows={8}
                  disabled={true}
                  style={{ fontFamily: "monospace", whiteSpace: "pre" }}
                />
              );
            }
          })()
          }
        </InputSidebarContainer>

        {details.Contenido && (
          <SectionRow>
            <div className="float-right">
              <IconButton onClick={() => handleCopyToClipboard(details.Contenido)}>
                <ToolTip message="Copiar contenido">
                  <div>
                    <SvgComponent
                      iconName="copy"
                      height="25"
                      width="25"
                    />
                  </div>
                </ToolTip>
              </IconButton>
            </div>
          </SectionRow>
        )}

        <InputSidebarContainer label="Respuesta">
          {details.Respuesta && typeof details.Respuesta === "string" && details.Respuesta.trim().startsWith("<") ? (
            <div className="col-sm-12">
              <XMLViewer
                xml={details.Respuesta}
                theme={{ overflowBreak: true }}
              />
            </div>
          ) : details.Respuesta && (() => {
            let parsed;
            try {
              parsed = typeof details.Respuesta === "object"
                ? details.Respuesta
                : JSON.parse(details.Respuesta);
            } catch {
              parsed = null;
            }
            if (parsed && typeof parsed === "object") {
              return (
                <div className="col-sm-12">
                  <ReactJson
                    src={parsed}
                    name={false}
                    collapsed={false}
                    enableClipboard={false}
                    displayDataTypes={false}
                    style={{ fontSize: "14px" }}
                  />
                </div>
              );
            } else {
              return (
                <Form.Control
                  as="textarea"
                  value={details.Respuesta}
                  rows={8}
                  disabled={true}
                  style={{ fontFamily: "monospace", whiteSpace: "pre" }}
                />
              );
            }
          })()
          }
        </InputSidebarContainer>

        {details.Respuesta && (
          <SectionRow>
            <div className="float-right">
              <IconButton onClick={() => handleCopyToClipboard(details.Respuesta)}>
                <ToolTip message="Copiar Respuesta">
                  <div>
                    <SvgComponent
                      iconName="copy"
                      height="25"
                      width="25"
                    />
                  </div>
                </ToolTip>
              </IconButton>
            </div>
          </SectionRow>
        )}
      </EditSection>
    </Simplebar>
  );
}